export const LEGACY_MAP_WIDTH = 900;
export const LEGACY_MAP_HEIGHT = 600;
export const COORDINATE_CONTRACT_VERSION = "terminal-coordinate-contract-v1";
export const DISPLAY_BBOX_TRANSFORM_VERSION = "web-mercator-bbox-display-v1";

const MAX_MERCATOR_LAT = 85.05112878;

function mercatorY(latitude) {
  const clamped = Math.max(-MAX_MERCATOR_LAT, Math.min(MAX_MERCATOR_LAT, Number(latitude)));
  const radians = clamped * Math.PI / 180;
  return Math.log(Math.tan(Math.PI / 4 + radians / 2));
}

function inverseMercatorY(value) {
  return (2 * Math.atan(Math.exp(value)) - Math.PI / 2) * 180 / Math.PI;
}

export function isValidBbox(bbox) {
  if (!Array.isArray(bbox) || bbox.length !== 4 || bbox.some(v => !Number.isFinite(Number(v)))) return false;
  const [west, south, east, north] = bbox.map(Number);
  return west >= -180 && east <= 180 && south >= -90 && north <= 90 && west < east && south < north;
}

export function legacyToLonLat(point, bbox, width = LEGACY_MAP_WIDTH, height = LEGACY_MAP_HEIGHT) {
  if (!isValidBbox(bbox)) return null;
  const [west, south, east, north] = bbox.map(Number);
  const [x, y] = point.map(Number);
  const longitude = west + (x / width) * (east - west);
  const northY = mercatorY(north);
  const southY = mercatorY(south);
  const latitude = inverseMercatorY(northY + (y / height) * (southY - northY));
  return [longitude, latitude];
}

export function lonLatToLegacy(point, bbox, width = LEGACY_MAP_WIDTH, height = LEGACY_MAP_HEIGHT) {
  if (!isValidBbox(bbox)) return null;
  const [west, south, east, north] = bbox.map(Number);
  const [longitude, latitude] = point.map(Number);
  const x = ((longitude - west) / (east - west)) * width;
  const northY = mercatorY(north);
  const southY = mercatorY(south);
  const y = ((mercatorY(latitude) - northY) / (southY - northY)) * height;
  return [Math.round(x * 10000) / 10000, Math.round(y * 10000) / 10000];
}

export function annotationLonLatPoints(annotation, bbox) {
  return annotationGeographicGeometry(annotation, bbox).points;
}

export function annotationGeographicGeometry(annotation, bbox) {
  if (Array.isArray(annotation?.points_geo) && annotation.points_geo.length) return {
    points: annotation.points_geo,
    spatialReference: annotation.spatial_reference || "EPSG:4326",
    quality: annotation.geographic_quality || "explicit_source",
    transformVersion: annotation.geographic_transform_version || null,
    operational: annotation.geographic_operational_use === true,
  };
  return {
    points: (annotation?.points || []).map(point => legacyToLonLat(point, bbox)).filter(Boolean),
    spatialReference: "EPSG:4326",
    quality: "bbox_display_approximation",
    transformVersion: DISPLAY_BBOX_TRANSFORM_VERSION,
    operational: false,
  };
}

export function isOperationalCoordinateContext(coordinateSystem, mapVersionId, entityMapVersionId) {
  const t = coordinateSystem?.tolerances || {};
  const v = coordinateSystem?.verification || {};
  return coordinateSystem?.contract_version === COORDINATE_CONTRACT_VERSION
    && coordinateSystem?.status === "verified"
    && coordinateSystem?.kind === "local_meters"
    && coordinateSystem?.units === "m"
    && coordinateSystem?.axes?.x === "east" && coordinateSystem?.axes?.y === "north"
    && Number.isFinite(Number(t.horizontal_accuracy_m))
    && Number(t.horizontal_accuracy_m) <= Number(t.operational_max_error_m)
    && Boolean(v.method && v.reviewed_by && v.reviewed_at)
    && Boolean(mapVersionId && coordinateSystem.map_version_id === mapVersionId && entityMapVersionId === mapVersionId);
}
